import React, { useEffect, useState } from 'react'
import { IoNotifications, IoNotificationsOff, IoCheckmark, IoAlert, IoSunny, IoMoon, IoPhonePortrait } from 'react-icons/io5'
import { useTheme, ACCENT_PRESETS, type ColorScheme } from '../context/ThemeContext'
import { useFocusTrap } from '../utils/a11y'
import { useOdds } from '../context/OddsContext'
import { BookmakerSelector } from './BookmakerSelector'
import { usePushNotifications } from '../hooks/usePushNotifications'
import { getAlertPreferences, updateAlertPreferences, type AlertPreferences } from '../api'
import type { OddsFormat } from '../types'

const ODDS_FORMAT_OPTIONS: { value: OddsFormat; label: string; example: string }[] = [
  { value: 'decimal', label: 'Decimal', example: '2.10' },
  { value: 'fractional', label: 'Fractional', example: '11/10' },
  { value: 'american', label: 'American', example: '+110' },
]

const COLOR_SCHEME_OPTIONS: { value: ColorScheme; label: string; icon: React.ReactNode }[] = [
  { value: 'light', label: 'Light', icon: <IoSunny size={16} /> },
  { value: 'dark', label: 'Dark', icon: <IoMoon size={16} /> },
  { value: 'system', label: 'System', icon: <IoPhonePortrait size={16} /> },
]

export function SettingsModal() {
  const { accentColor, setAccentColor, colorScheme, setColorScheme, isSettingsOpen, closeSettings } = useTheme()
  const modalRef = useFocusTrap<HTMLDivElement>(isSettingsOpen)
  const {
    bookmakers,
    bookmakersLoading,
    preferredBookmaker,
    setPreferredBookmaker,
    oddsFormat,
    setOddsFormat,
    oddsHidden,
    setOddsHidden,
    showValueBadges,
    setShowValueBadges,
    minEdgeThreshold,
    setMinEdgeThreshold,
  } = useOdds()

  // Push notification state
  const push = usePushNotifications()
  const [alertPrefs, setAlertPrefs] = useState<AlertPreferences | null>(null)
  const [alertPrefsLoading, setAlertPrefsLoading] = useState(false)

  // Load alert preferences when settings open
  useEffect(() => {
    if (isSettingsOpen && !alertPrefs) {
      setAlertPrefsLoading(true)
      getAlertPreferences()
        .then(setAlertPrefs)
        .catch((err) => console.error('Failed to load alert preferences:', err))
        .finally(() => setAlertPrefsLoading(false))
    }
  }, [isSettingsOpen, alertPrefs])

  // Update a single alert preference
  const handleAlertPrefChange = async (key: keyof AlertPreferences, value: boolean) => {
    if (!alertPrefs) return
    try {
      const updated = await updateAlertPreferences({ [key]: value })
      setAlertPrefs(updated)
    } catch (err) {
      console.error('Failed to update alert preference:', err)
    }
  }

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeSettings()
    }
    if (isSettingsOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isSettingsOpen, closeSettings])

  if (!isSettingsOpen) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="settings-backdrop"
        onClick={closeSettings}
        aria-hidden="true"
      />

      {/* Modal */}
      <div
        ref={modalRef}
        className="settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="settings-title"
      >
        <div className="settings-header">
          <span id="settings-title" className="settings-title">Settings</span>
          <button
            className="modal-close"
            onClick={closeSettings}
            aria-label="Close settings"
            type="button"
          >
            &times;
          </button>
        </div>

        <div className="settings-body">
          <div className="settings-section">
            <div className="settings-label">Accent Color</div>
            <div className="settings-description">
              Choose a color that highlights buttons, borders, and active elements
            </div>

            <div className="color-presets">
              {ACCENT_PRESETS.map((preset) => (
                <button
                  key={preset.color}
                  className={`color-preset ${accentColor === preset.color ? 'active' : ''}`}
                  style={{ '--preset-color': preset.color } as React.CSSProperties}
                  onClick={() => setAccentColor(preset.color)}
                  title={preset.name}
                >
                  {accentColor === preset.color && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              ))}
            </div>

            <div className="custom-color-row">
              <span className="custom-color-label">Custom color:</span>
              <div className="custom-color-input-wrapper">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                  className="custom-color-input"
                />
                <span className="custom-color-value">{accentColor.toUpperCase()}</span>
              </div>
            </div>
          </div>

          {/* Theme Mode Section */}
          <div className="settings-section">
            <div className="settings-label">Theme</div>
            <div className="settings-description">
              Choose light, dark, or follow your system preference
            </div>

            <div className="theme-mode-selector" role="radiogroup" aria-label="Theme mode">
              {COLOR_SCHEME_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  className={`theme-mode-option ${colorScheme === option.value ? 'active' : ''}`}
                  onClick={() => setColorScheme(option.value)}
                  role="radio"
                  aria-checked={colorScheme === option.value}
                  type="button"
                >
                  <span className="theme-mode-icon" aria-hidden="true">{option.icon}</span>
                  <span className="theme-mode-label">{option.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="settings-preview">
            <div className="preview-label">Preview</div>
            <div className="preview-items">
              <button className="add-btn" type="button">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add
              </button>
              <span className="chip chip-active">Selected Chip</span>
              <span className="tab active">Active Tab</span>
            </div>
          </div>

          {/* Odds Preferences Section */}
          <div className="settings-section">
            <div className="settings-label">Odds Preferences</div>
            <div className="settings-description">
              Choose your preferred bookmaker and odds format
            </div>

            <div className="settings-subsection">
              <div className="settings-toggle-row">
                <div className="settings-toggle-info">
                  <div className="settings-sublabel">Hide Odds</div>
                  <div className="settings-toggle-description">
                    Focus on stats without odds distractions
                  </div>
                </div>
                <button
                  className={`settings-toggle ${oddsHidden ? 'active' : ''}`}
                  onClick={() => setOddsHidden(!oddsHidden)}
                  type="button"
                  role="switch"
                  aria-checked={oddsHidden}
                >
                  <span className="settings-toggle-track">
                    <span className="settings-toggle-thumb" />
                  </span>
                </button>
              </div>
            </div>

            <div className={`settings-subsection ${oddsHidden ? 'disabled' : ''}`}>
              <div className="settings-sublabel">Preferred Bookmaker</div>
              {bookmakersLoading ? (
                <div className="settings-loading">Loading bookmakers...</div>
              ) : (
                <BookmakerSelector
                  bookmakers={bookmakers}
                  value={preferredBookmaker?.slug || null}
                  onChange={setPreferredBookmaker}
                  disabled={oddsHidden}
                />
              )}
            </div>

            <div className={`settings-subsection ${oddsHidden ? 'disabled' : ''}`}>
              <div className="settings-sublabel">Odds Format</div>
              <div className="odds-format-options">
                {ODDS_FORMAT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    className={`odds-format-option ${oddsFormat === option.value ? 'active' : ''}`}
                    onClick={() => !oddsHidden && setOddsFormat(option.value)}
                    type="button"
                    disabled={oddsHidden}
                  >
                    <span className="odds-format-label">{option.label}</span>
                    <span className="odds-format-example">{option.example}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Value Betting Section */}
            <div className={`settings-subsection ${oddsHidden ? 'disabled' : ''}`}>
              <div className="settings-toggle-row">
                <div className="settings-toggle-info">
                  <div className="settings-sublabel">Show Value Badges</div>
                  <div className="settings-toggle-description">
                    Display edge % next to odds (green = value bet)
                  </div>
                </div>
                <button
                  className={`settings-toggle ${showValueBadges ? 'active' : ''}`}
                  onClick={() => !oddsHidden && setShowValueBadges(!showValueBadges)}
                  type="button"
                  role="switch"
                  aria-checked={showValueBadges}
                  disabled={oddsHidden}
                >
                  <span className="settings-toggle-track">
                    <span className="settings-toggle-thumb" />
                  </span>
                </button>
              </div>
            </div>

            <div className={`settings-subsection ${oddsHidden || !showValueBadges ? 'disabled' : ''}`}>
              <div className="settings-sublabel">Minimum Edge Threshold</div>
              <div className="settings-toggle-description">
                Only highlight as value when edge exceeds this
              </div>
              <div className="edge-threshold-options">
                {[0, 3, 5, 10].map((threshold) => (
                  <button
                    key={threshold}
                    className={`edge-threshold-option ${minEdgeThreshold === threshold ? 'active' : ''}`}
                    onClick={() => setMinEdgeThreshold(threshold)}
                    type="button"
                    disabled={oddsHidden || !showValueBadges}
                  >
                    {threshold === 0 ? 'All' : `${threshold}%+`}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="settings-section">
            <div className="settings-label">Notifications</div>
            <div className="settings-description">
              Get alerted about lineups, builder issues, and booking risks
            </div>

            {/* Push Notifications Toggle */}
            <div className="settings-subsection">
              <div className="settings-toggle-row">
                <div className="settings-toggle-info">
                  <div className="settings-sublabel">
                    {push.subscribed ? (
                      <span className="push-status-label">
                        <IoNotifications size={16} className="push-icon active" />
                        Push Notifications
                      </span>
                    ) : (
                      <span className="push-status-label">
                        <IoNotificationsOff size={16} className="push-icon" />
                        Push Notifications
                      </span>
                    )}
                  </div>
                  <div className="settings-toggle-description">
                    {!push.supported ? (
                      <span className="push-error">Not supported in this browser</span>
                    ) : push.permission === 'denied' ? (
                      <span className="push-error">Blocked - check browser settings</span>
                    ) : push.error ? (
                      <span className="push-error">{push.error}</span>
                    ) : push.subscribed ? (
                      <span className="push-success">Enabled - you'll receive alerts</span>
                    ) : (
                      'Receive alerts even when app is closed'
                    )}
                  </div>
                </div>
                <button
                  className={`settings-toggle ${push.subscribed ? 'active' : ''}`}
                  onClick={push.toggle}
                  type="button"
                  role="switch"
                  aria-checked={push.subscribed}
                  disabled={!push.supported || push.loading || push.permission === 'denied'}
                >
                  <span className="settings-toggle-track">
                    <span className="settings-toggle-thumb">
                      {push.loading && <span className="toggle-loading" />}
                    </span>
                  </span>
                </button>
              </div>
            </div>

            {/* Alert Type Toggles */}
            {alertPrefsLoading ? (
              <div className="settings-loading">Loading preferences...</div>
            ) : alertPrefs ? (
              <>
                <div className="settings-subsection">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <div className="settings-sublabel">Lineup Alerts</div>
                      <div className="settings-toggle-description">
                        When starting XI is confirmed for your saved builders
                      </div>
                    </div>
                    <button
                      className={`settings-toggle ${alertPrefs.lineup_alerts ? 'active' : ''}`}
                      onClick={() => handleAlertPrefChange('lineup_alerts', !alertPrefs.lineup_alerts)}
                      type="button"
                      role="switch"
                      aria-checked={alertPrefs.lineup_alerts}
                    >
                      <span className="settings-toggle-track">
                        <span className="settings-toggle-thumb" />
                      </span>
                    </button>
                  </div>
                </div>

                <div className="settings-subsection">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <div className="settings-sublabel">Builder Alerts</div>
                      <div className="settings-toggle-description">
                        When a player in your builder becomes unavailable
                      </div>
                    </div>
                    <button
                      className={`settings-toggle ${alertPrefs.builder_alerts ? 'active' : ''}`}
                      onClick={() => handleAlertPrefChange('builder_alerts', !alertPrefs.builder_alerts)}
                      type="button"
                      role="switch"
                      aria-checked={alertPrefs.builder_alerts}
                    >
                      <span className="settings-toggle-track">
                        <span className="settings-toggle-thumb" />
                      </span>
                    </button>
                  </div>
                </div>

                <div className="settings-subsection">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <div className="settings-sublabel">Booking Warnings</div>
                      <div className="settings-toggle-description">
                        When a player is at risk of a second yellow
                      </div>
                    </div>
                    <button
                      className={`settings-toggle ${alertPrefs.foul_warnings ? 'active' : ''}`}
                      onClick={() => handleAlertPrefChange('foul_warnings', !alertPrefs.foul_warnings)}
                      type="button"
                      role="switch"
                      aria-checked={alertPrefs.foul_warnings}
                    >
                      <span className="settings-toggle-track">
                        <span className="settings-toggle-thumb" />
                      </span>
                    </button>
                  </div>
                </div>

                <div className="settings-subsection">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <div className="settings-sublabel">Goal Alerts</div>
                      <div className="settings-toggle-description">
                        Get notified when goals are scored in tracked fixtures
                      </div>
                    </div>
                    <button
                      className={`settings-toggle ${alertPrefs.goal_alerts ? 'active' : ''}`}
                      onClick={() => handleAlertPrefChange('goal_alerts', !alertPrefs.goal_alerts)}
                      type="button"
                      role="switch"
                      aria-checked={alertPrefs.goal_alerts}
                    >
                      <span className="settings-toggle-track">
                        <span className="settings-toggle-thumb" />
                      </span>
                    </button>
                  </div>
                </div>

                <div className="settings-subsection">
                  <div className="settings-toggle-row">
                    <div className="settings-toggle-info">
                      <div className="settings-sublabel">Card Alerts</div>
                      <div className="settings-toggle-description">
                        Get notified about yellow and red cards
                      </div>
                    </div>
                    <button
                      className={`settings-toggle ${alertPrefs.card_alerts ? 'active' : ''}`}
                      onClick={() => handleAlertPrefChange('card_alerts', !alertPrefs.card_alerts)}
                      type="button"
                      role="switch"
                      aria-checked={alertPrefs.card_alerts}
                    >
                      <span className="settings-toggle-track">
                        <span className="settings-toggle-thumb" />
                      </span>
                    </button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </>
  )
}
